import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/query/query-keys';

interface SendTripToAccountantVariables {
  tripId: string;
}

interface ApiErrorPayload {
  error?: {
    message?: string;
  };
}

function extractErrorMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object' && 'error' in (payload as Record<string, unknown>)) {
    const parsed = payload as ApiErrorPayload;
    const message = parsed.error?.message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }

  return fallback;
}

async function sendTripToAccountant({ tripId }: SendTripToAccountantVariables): Promise<void> {
  const response = await apiFetch(`/api/v1/business-trip-requests/${tripId}/send-to-accountant`, {
    method: 'POST',
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const message = extractErrorMessage(payload, 'Failed to send trip to accountant');
    throw new Error(message);
  }
}

export function useSendTripToAccountant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendTripToAccountant,
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trips });
      queryClient.invalidateQueries({ queryKey: queryKeys.tripApprovals });
      queryClient.invalidateQueries({ queryKey: queryKeys.trip(variables.tripId) });
    },
    onError: (error) => {
      console.error('Error sending trip to accountant:', error);
    },
  });
}
